<template>
  <div class="q-pa-md relative-position" style="min-height: 220px;">
    <q-inner-loading :showing="loading">
      <loading-spinner />
    </q-inner-loading>

    <div
      v-if="showQuickActions"
      class="q-mb-md"
    >
      <q-tabs
        shrink
        outside-arrows
        mobile-arrows
        :breakpoint="0"
      >
        <stacked-btn
          v-if="can(permissions.canAddInvoices)"
          bubble
          icon="mdi-cash"
          label="New Invoice"
          style="width: 100px;"
          class="full-height"
          @click="$emit('newInvoice')"
        />
        <stacked-btn
          v-if="can(permissions.canAddQuotations)"
          bubble
          icon="mdi-file-document"
          label="New Quote"
          style="width: 100px;"
          class="full-height"
          @click="$emit('newQuotation')"
        />
        <stacked-btn
          v-if="can(permissions.canManagePayments)"
          bubble
          icon="mdi-cash-multiple"
          label="Take Payment"
          color="green-7"
          style="width: 100px;"
          class="full-height"
          @click="$refs.invoiceActions.takePayment()"
        />
        <stacked-btn
          v-if="can(permissions.canManagePayments)"
          bubble
          icon="mdi-bell-outline"
          label="Send Reminder"
          color="teal-8"
          style="width: 100px;"
          class="full-height"
          @click="$refs.invoiceActions.sendReminder()"
        />
      </q-tabs>
      <q-separator class="q-mt-sm" />
    </div>

    <template v-if="!loading">
      <no-results
        v-if="invoices.length === 0 && quotations.length === 0"
        icon="mdi-receipt-text-outline"
        title="No invoices or quotes"
        subtitle="Invoices and quotations linked to this appointment will appear here."
        class="full-width"
      />

      <template v-else>
        <q-tabs
          v-model="billingTab"
          dense
          no-caps
          align="left"
          active-color="blue"
          indicator-color="blue"
          class="text-grey-7"
        >
          <q-tab
            name="invoices"
            :label="invoices.length ? 'Invoices (' + invoices.length + ')' : 'Invoices'"
          />
          <q-tab
            name="quotations"
            :label="quotations.length ? 'Quotations (' + quotations.length + ')' : 'Quotations'"
          />
        </q-tabs>
        <q-separator class="q-mb-md" />

        <q-tab-panels
          v-model="billingTab"
          class="overflow-panels"
        >
          <!-- INVOICES -->
          <q-tab-panel
            name="invoices"
            class="q-pa-none"
          >
            <no-results
              v-if="invoices.length === 0"
              icon="mdi-receipt-text-outline"
              title="No invoices"
              class="full-width"
            />
            <template v-else>
              <q-input
                v-if="invoices.length > 5"
                v-model="invoiceFilter"
                dense
                outlined
                clearable
                debounce="200"
                placeholder="Filter invoices..."
                class="q-mb-md"
              >
                <template #prepend>
                  <q-icon name="mdi-magnify" />
                </template>
              </q-input>

              <q-item-label
                v-if="invoiceFilter && filteredInvoices.length === 0"
                caption
                class="q-py-md"
              >
                No invoices match "{{ invoiceFilter }}".
              </q-item-label>

              <q-list
                v-if="filteredInvoices.length"
                bordered
                separator
                class="rounded-borders"
              >
                <q-item
                  v-for="invoice in filteredInvoices"
                  :key="'invoice-' + invoice.id"
                  class="q-py-md"
                >
                  <q-item-section>
                    <q-item-label class="text-weight-medium">
                      {{ invoice.invoice_number }}
                    </q-item-label>
                    <q-item-label
                      v-if="invoice.client"
                      caption
                      lines="1"
                    >
                      {{ invoice.client }}
                    </q-item-label>
                  </q-item-section>

                  <q-item-section side class="gt-xs">
                    <InvoiceStatus :invoice="invoice" />
                  </q-item-section>

                  <q-item-section
                    side
                    class="text-right"
                    style="min-width: 78px;"
                  >
                    <q-item-label class="text-weight-bold">
                      {{ friendlyNumber(invoice.total, true) }}
                    </q-item-label>
                  </q-item-section>

                  <q-item-section
                    side
                    :style="can(permissions.canManagePayments) ? 'min-width: 120px;' : null"
                  >
                    <div class="row items-center justify-end no-wrap q-gutter-xs">
                      <q-btn
                        v-if="canTakePayment(invoice)"
                        dense
                        flat
                        round
                        icon="mdi-cash-multiple"
                        color="green-7"
                        @click="$refs.invoiceActions.takePayment(invoice.id)"
                      >
                        <q-tooltip>Take payment</q-tooltip>
                      </q-btn>
                      <q-btn
                        v-if="canSendReminder(invoice)"
                        dense
                        flat
                        round
                        icon="mdi-bell-outline"
                        color="teal-8"
                        @click="$refs.invoiceActions.sendReminder(invoice.id)"
                      >
                        <q-tooltip>Send payment reminder</q-tooltip>
                      </q-btn>
                      <q-btn
                        dense
                        flat
                        round
                        icon="mdi-eye-outline"
                        color="blue-7"
                        @click="goTo('invoicing/view?invoice_id=' + invoice.id)"
                      >
                        <q-tooltip>View invoice</q-tooltip>
                      </q-btn>
                    </div>
                  </q-item-section>
                </q-item>
              </q-list>
            </template>
          </q-tab-panel>

          <!-- QUOTATIONS -->
          <q-tab-panel
            name="quotations"
            class="q-pa-none"
          >
            <no-results
              v-if="quotations.length === 0"
              icon="mdi-file-document-outline"
              title="No quotations"
              class="full-width"
            />
            <template v-else>
              <q-input
                v-if="quotations.length > 5"
                v-model="quotationFilter"
                dense
                outlined
                clearable
                debounce="200"
                placeholder="Filter quotations..."
                class="q-mb-md"
              >
                <template #prepend>
                  <q-icon name="mdi-magnify" />
                </template>
              </q-input>

              <q-item-label
                v-if="quotationFilter && filteredQuotations.length === 0"
                caption
                class="q-py-md"
              >
                No quotations match "{{ quotationFilter }}".
              </q-item-label>

              <q-list
                v-if="filteredQuotations.length"
                bordered
                separator
                class="rounded-borders"
              >
                <q-item
                  v-for="quotation in filteredQuotations"
                  :key="'quotation-' + quotation.id"
                  class="q-py-md"
                >
                  <q-item-section>
                    <q-item-label class="text-weight-medium">
                      {{ quotation.quotation_number }}
                    </q-item-label>
                    <q-item-label
                      v-if="quotation.client"
                      caption
                      lines="1"
                    >
                      {{ quotation.client }}
                    </q-item-label>
                  </q-item-section>

                  <q-item-section side class="gt-xs">
                    <QuotationStatus :quotation="quotation" />
                  </q-item-section>

                  <q-item-section
                    side
                    class="text-right"
                    style="min-width: 78px;"
                  >
                    <q-item-label class="text-weight-bold">
                      {{ friendlyNumber(quotation.total, true) }}
                    </q-item-label>
                  </q-item-section>

                  <q-item-section side>
                    <q-btn
                      dense
                      flat
                      round
                      icon="mdi-eye-outline"
                      color="blue-7"
                      @click="viewingQuotationId = quotation.id"
                    >
                      <q-tooltip>View quotation</q-tooltip>
                    </q-btn>
                  </q-item-section>
                </q-item>
              </q-list>
            </template>
          </q-tab-panel>
        </q-tab-panels>
      </template>
    </template>

    <ViewQuotationDialog
      :id="viewingQuotationId"
      without-appointment
      :disable-editing="false"
      @close="viewingQuotationId = null"
      @deleted="loadBilling"
    />

    <InvoiceActions
      ref="invoiceActions"
      :appointment-id="appointmentId"
      @updated="onPaid"
    />
  </div>
</template>

<script>
import { defineAsyncComponent } from 'vue'

import InvoiceStatus from 'components/app/invoicing/partials/Status'
import QuotationStatus from 'components/app/quotations/partials/Status'
import InvoiceActions from './partials/InvoiceActions'

/*
  Surfaces the invoices and quotations linked to an appointment with direct
  "view", "take payment" and "send reminder" actions, so this isn't buried in
  the activity timeline. Data comes from the appointment timeline (filtered to
  billing entries); the full invoice is fetched on demand for payment/reminder
  because the timeline summary lacks the fields those dialogs need.
*/
export default {
  components: {
    InvoiceStatus,
    QuotationStatus,
    InvoiceActions,
    ViewQuotationDialog: defineAsyncComponent(() => import('components/app/quotations/partials/view/Dialog'))
  },
  props: {
    appointmentId: {
      type: Number,
      required: true
    }
  },
  emits: ['updated', 'newInvoice', 'newQuotation'],
  data () {
    return {
      loading: false,
      invoices: [],
      quotations: [],
      billingTab: 'invoices',
      invoiceFilter: '',
      quotationFilter: '',
      viewingQuotationId: null
    }
  },
  computed: {
    showQuickActions () {
      return this.can(this.permissions.canAddInvoices) ||
        this.can(this.permissions.canAddQuotations) ||
        this.can(this.permissions.canManagePayments)
    },
    filteredInvoices () {
      return this.filterList(this.invoices, 'invoice_number', this.invoiceFilter)
    },
    filteredQuotations () {
      return this.filterList(this.quotations, 'quotation_number', this.quotationFilter)
    }
  },
  methods: {
    async loadBilling () {
      this.loading = true
      const { status, payload } = await this.$get('v1/appointments/timeline', {
        id: this.appointmentId
      })
      this.loading = false
      if (status === 'success') {
        const entries = payload.timeline || []
        this.invoices = entries.filter(entry => entry.timeline_type === 'invoice')
        this.quotations = entries.filter(entry => entry.timeline_type === 'quotation')
        // Open on whichever tab actually has something.
        if (!this.invoices.length && this.quotations.length) {
          this.billingTab = 'quotations'
        }
      }
    },
    filterList (list, numberField, search) {
      const term = (search || '').toLowerCase().trim()
      if (!term) {
        return list
      }
      return list.filter(item =>
        (item[numberField] || '').toLowerCase().includes(term) ||
        (item.client || '').toLowerCase().includes(term)
      )
    },
    canTakePayment (invoice) {
      return this.can(this.permissions.canManagePayments) &&
        (invoice.status === 'OPEN' || invoice.status === 'PARTIAL')
    },
    canSendReminder (invoice) {
      return this.can(this.permissions.canManagePayments) &&
        (invoice.status === 'OPEN' || invoice.status === 'PARTIAL')
    },
    onPaid () {
      this.loadBilling()
      // Let the appointment refresh its invoiced/paid status badges.
      this.$emit('updated')
    }
  },
  mounted () {
    this.loadBilling()
  }
}
</script>
